<template>
  <div class="relative my-4 h-10">
    <div class="flex border border-gray-300 bg-white rounded-md h-full overflow-hidden" :class="{ 'rounded-b-none': autocompleting, shadow: emphasize }">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <svg class="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z" clip-rule="evenodd" />
        </svg>
      </div>
      <label for="inputDomain" class="sr-only">Domain name</label>
      <input class="w-full min-w-0 pl-11 outline-none" @input="autocomplete" @keydown.up.prevent="move(-1)" @keydown.down.prevent="move(1)"
             @keydown.esc="cancelSelection" @focus="hasFocus = true" @blur="hasFocus = false"
             :value="visibleInput" type="url" id="inputDomain" ref="inputDomain"
             :placeholder="placeholderText" autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false">
      <div class="grow-0 shrink-0">
        <button :class="['h-full align-middle px-4 whitespace-nowrap border-gray-300 border-l',
                         emphasize ? 'text-white bg-[#3a0161] hover:bg-[#23013c]' : 'text-gray-800 hover:text-white hover:bg-[#3a0161]']"
                type="submit" @click.stop.prevent="redirectToDnsRecords">
          {{ buttonText }}
        </button>
      </div>
    </div>
    <ul v-show="autocompleting" @mousedown.capture.prevent
        class="absolute border-box p-0 w-full overflow-hidden z-[1001] text-left text-gray-500 bg-white border border-t-0 border-gray-300 rounded-b-md">
      <li v-for="(autocompleteDomain, index) in autocompleteDomains" :key="`${autocompleteDomain.prefix}${autocompleteDomain.completion}`"
          @click="redirectTo(index)" @mouseover="select(index)" @focus="select(index)"
          class="px-3 py-1.5 list-none cursor-pointer"
          :class="{ 'bg-gray-200': index === selected }">
        <img class="h-4 w-4 inline inset-y-0 left-0 mr-4"
             :src="`https://icons.duckduckgo.com/ip3/${autocompleteDomain.prefix}${autocompleteDomain.completion}.ico`"
             alt="">{{ autocompleteDomain.prefix }}<span class="text-gray-800">{{ autocompleteDomain.completion }}</span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import axios, { AxiosResponse, Canceler } from 'axios';
import {
  ref, computed, onMounted, watch,
} from 'vue';

const props = defineProps({
  autofocus: Boolean,
  emphasize: Boolean,
  autocompleteEnabled: {
    type: Boolean,
    default: true,
  },
  prefill: {
    type: String,
    default: '',
  },
  buttonText: {
    type: String,
    default: 'Find DNS records',
  },
  placeholderText: {
    type: String,
    default: 'Domain name',
  },
});

const emit = defineEmits(['submitted']);

type AutocompleteDomain = {
  prefix: string;
  completion: string;
}

const autocompleteUrlPrefix = 'autocomplete/';

const autocompleteDomains = ref(new Array<AutocompleteDomain>());
const inputDomain = ref<HTMLInputElement | null>(null);
const cancelToken = ref<Canceler | null>(null);
const visibleInput = ref('');
const prefix = ref('');
const selected = ref(-1);
const hasFocus = ref(false);

// see https://github.com/vuejs/vue/issues/9299#issuecomment-481530279
function autocomplete(evt: Event): void {
  const target = evt.target as HTMLInputElement;
  prefix.value = target.value;
  visibleInput.value = target.value;

  if (!props.autocompleteEnabled) {
    return;
  }

  selected.value = -1;
  if (visibleInput.value === '') {
    autocompleteDomains.value = [];
  } else {
    if (cancelToken.value) {
      cancelToken.value();
    }
    const axiosSource = axios.CancelToken.source();
    cancelToken.value = axiosSource.cancel;
    axios.get(`${autocompleteUrlPrefix}${visibleInput.value}`, {
      cancelToken: axiosSource.token,
    }).then((response: AxiosResponse) => {
      const { url } = response.config;
      if (url) {
        const fetchedPrefix = url.substr(autocompleteUrlPrefix.length, url.length);
        // Prevent race conditions
        if (fetchedPrefix === prefix.value) {
          if (response.data === '') {
            autocompleteDomains.value = [];
          } else {
            autocompleteDomains.value = response.data.split('\n')
              .flatMap((result: string) => [{
                prefix: visibleInput.value,
                completion: result,
              }]);
          }
        }
      }
    }).catch((error) => {
      if (!axios.isCancel(error)) {
        // eslint-disable-next-line
        console.log(error.message);
      }
    });
  }
}

function select(index: number, updateInput = false): void {
  if (updateInput) {
    visibleInput.value = autocompleteDomains.value[index].prefix + autocompleteDomains.value[index].completion;
  }
  selected.value = index;
}

function cancelSelection(): void {
  visibleInput.value = prefix.value;
  selected.value = -1;
}

function move(direction: number): boolean {
  if (selected.value === -1) {
    if (direction === -1) {
      select(autocompleteDomains.value.length - 1, true);
    } else {
      select(0, true);
    }
  } else {
    select((selected.value + autocompleteDomains.value.length + direction) % autocompleteDomains.value.length, true);
  }
  return false;
}

function redirectTo(index: number): void {
  const input = inputDomain.value as HTMLInputElement;
  input.blur();
  emit('submitted', autocompleteDomains.value[index].prefix + autocompleteDomains.value[index].completion);
}

function redirectToDnsRecords(): void {
  if (visibleInput.value !== '') {
    const input = inputDomain.value as HTMLInputElement;
    input.blur();
    emit('submitted', visibleInput.value);
  }
}

const autocompleting = computed(() => autocompleteDomains.value.length > 0 && hasFocus.value);

onMounted(() => {
  if (props.autofocus) {
    const input = inputDomain.value as HTMLInputElement;
    input.focus();
  }
});

watch(() => props.prefill, () => {
  visibleInput.value = props.prefill;
}, {
  immediate: true,
});
</script>
